import { create } from 'zustand';
import {
	SimulateVanCreditInput,
	DaysPerSimulationPeriod,
	Request
} from '@/graphql/__generated__/van-credits';
import { parseAccountingToMoney } from '@/utils/utils';

interface SimulationStore {
	formData: SimulateVanCreditInput;
	initialFormData: SimulateVanCreditInput | null;
	hasUserInteracted: boolean;
	setFormData: (data: SimulateVanCreditInput) => void;
	updateField: (key: keyof SimulateVanCreditInput, value: any) => void;
	initializeFromData: (data: Request) => void;
	resetForm: () => void;
	simulate: () => void;
}

const defaultFormData: SimulateVanCreditInput = {
	requestId: '',
	amountRequested: '0.00 MXN',
	paymentCapacity: '0.00 MXN',
	daysPerPeriod: DaysPerSimulationPeriod.MONTHLY,
	firstPaymentDate: '',
	sameDayPayment: false,
	percentageOfCreditToSettle: '50.0',
	basisPoints: '0.0',
	gpsAmount: '0.00 MXN',
	insuranceAmount: '0.00 MXN'
};

const isCalculationRelevantField = (key: keyof SimulateVanCreditInput): boolean => {
	return [
		'paymentCapacity',
		'amountRequested',
		'daysPerPeriod',
		'percentageOfCreditToSettle',
		'basisPoints',
		'gpsAmount',
		'insuranceAmount'
	].includes(key);
};

export const useSimulationStore = create<SimulationStore>((set, get) => ({
	formData: defaultFormData,
	initialFormData: null,
	hasUserInteracted: false,

	setFormData: (data) => set({ formData: data }),

	updateField: (key, value) => {
		const currentData = get().formData;
		const { initialFormData } = get();

		let processedValue = value;
		if (
			key === 'amountRequested' ||
			key === 'paymentCapacity' ||
			key === 'gpsAmount' ||
			key === 'insuranceAmount'
		) {
			let cleanValue = value;
			cleanValue = cleanValue.replace(/[$MX\s]/g, '');
			cleanValue = cleanValue.replace(/MXN/g, '');
			cleanValue = cleanValue.replace(/,/g, '');

			const numValue = parseFloat(cleanValue);
			if (!isNaN(numValue)) {
				processedValue = `${numValue.toFixed(2)} MXN`;
			}
		}

		const newFormData = {
			...currentData,
			[key]: processedValue
		};

		let hasInteracted = false;
		if (initialFormData && isCalculationRelevantField(key)) {
			const calculationFields: (keyof SimulateVanCreditInput)[] = [
				'paymentCapacity',
				'amountRequested',
				'daysPerPeriod',
				'percentageOfCreditToSettle'
			];

			hasInteracted = calculationFields.some((field) => {
				const newValue = field === key ? processedValue : newFormData[field];
				return newValue !== initialFormData[field];
			});
		}

		set({
			formData: newFormData,
			hasUserInteracted: hasInteracted || get().hasUserInteracted
		});
	},

	initializeFromData: (data) => {
		const simulationConfig = data.simulation?.configuration;

		const ensureMoneyFormat = (value?: string | null): string => {
			if (!value) return '0.00 MXN';
			if (value.includes('MXN') && !value.includes('$') && !value.includes(',')) {
				return value;
			}

			let cleanValue = value;
			cleanValue = cleanValue.replace(/[$MX\s]/g, '');
			cleanValue = cleanValue.replace(/MXN/g, '');
			cleanValue = cleanValue.replace(/,/g, '');

			const numValue = parseFloat(cleanValue);
			return isNaN(numValue) ? '0.00 MXN' : `${numValue.toFixed(2)} MXN`;
		};

		const convertToPercentage = (defaultVal: string, value?: string | number | null): string => {
			if (value === null || value === undefined) return defaultVal;
			const numValue = typeof value === 'string' ? parseFloat(value) : value;
			if (isNaN(numValue)) return defaultVal;

			if (numValue < 1) {
				return (numValue * 100).toFixed(1);
			}
			return numValue.toFixed(1);
		};

		const ensureDateFormat = (value?: string | null): string => {
			if (!value) return '';

			if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
				return value;
			}

			try {
				const date = new Date(value);
				if (!isNaN(date.getTime())) {
					return date.toISOString().split('T')[0];
				}
			} catch (error) {
				console.warn('Failed to parse date:', value, error);
			}

			return '';
		};

		const calculateDefaultFirstPaymentDate = (daysPerPeriod: DaysPerSimulationPeriod): string => {
			try {
				switch (daysPerPeriod) {
					case DaysPerSimulationPeriod.BIWEEKLY:
						return new Date(new Date().setDate(new Date().getDate() + 15))
							.toISOString()
							.split('T')[0];
					case DaysPerSimulationPeriod.WEEKLY:
						return new Date(new Date().setDate(new Date().getDate() + 7))
							.toISOString()
							.split('T')[0];
					case DaysPerSimulationPeriod.MONTHLY:
						return new Date(new Date().setDate(new Date().getDate() + 30))
							.toISOString()
							.split('T')[0];
					default:
						return '';
				}
			} catch (error) {
				console.error('Error calculating default first payment date:', error);
				return '';
			}
		};

		const determinedDaysPerPeriod = simulationConfig?.daysPerPeriod
			? simulationConfig.daysPerPeriod === 7
				? DaysPerSimulationPeriod.WEEKLY
				: simulationConfig.daysPerPeriod === 15
					? DaysPerSimulationPeriod.BIWEEKLY
					: DaysPerSimulationPeriod.MONTHLY
			: DaysPerSimulationPeriod.MONTHLY;

		const configuredFirstPaymentDate = ensureDateFormat(simulationConfig?.firstPaymentDate);
		const finalFirstPaymentDate =
			configuredFirstPaymentDate || calculateDefaultFirstPaymentDate(determinedDaysPerPeriod);

		const finalFormData = {
			requestId: data.requestId || '',
			amountRequested: ensureMoneyFormat(
				simulationConfig?.amountRequested || data.creditDetails?.amountRequested
			),
			paymentCapacity: ensureMoneyFormat(
				simulationConfig?.paymentCapacity || parseAccountingToMoney(data.calculatedNetIncome ?? '')
			),
			daysPerPeriod: determinedDaysPerPeriod,
			firstPaymentDate: finalFirstPaymentDate,
			sameDayPayment: simulationConfig?.sameDayPayment ?? true,
			percentageOfCreditToSettle: convertToPercentage(
				'50.0',
				simulationConfig?.percentageOfCreditToSettle
			),
			basisPoints: convertToPercentage('20.0', simulationConfig?.basisPoints),
			gpsAmount: ensureMoneyFormat(simulationConfig?.gpsAmount),
			insuranceAmount: ensureMoneyFormat(simulationConfig?.insuranceAmount)
		};

		set({
			formData: finalFormData,
			initialFormData: { ...finalFormData },
			hasUserInteracted: false
		});
	},

	resetForm: () =>
		set({
			formData: defaultFormData,
			initialFormData: null,
			hasUserInteracted: false
		}),

	simulate: () => {
		const { formData } = get();
		console.log('Simulating with data:', formData);
	}
}));
